/**
 * Filtre de famille du configurateur produit.
 *
 * L'etape 1 (famille de tissu) n'est pas une option de variante : c'est un
 * filtre d'affichage sur l'etape 2. Elle ne touche donc jamais aux <input
 * radio> elle-meme, et la selection de variante reste entierement geree par
 * VariantSelects.
 *
 * La famille ouverte au chargement est calculee cote serveur a partir de la
 * valeur cochee : apres un re-rendu par la Section Rendering API, le composant
 * se rebranche sur un DOM deja dans le bon etat, sans rien a restaurer.
 *
 * Deux entrees distinctes, et la distinction compte :
 *
 *   apply(family)  n'ouvre que l'affichage. C'est ce qu'appellent les
 *                  composants qui vont eux-memes cocher une valeur precise
 *                  (<go-configurator-link>, <go-visual-carousel>) : ouvrir la
 *                  famille ET choisir un coloris ferait deux selections, donc
 *                  deux re-rendus, dont un vers la mauvaise variante ;
 *   select(family) coche le premier coloris de la famille. C'est ce que fait un
 *                  clic sur un bouton de famille : sans cela le visuel, le prix
 *                  et le recapitulatif resteraient sur l'ancien coloris — donc
 *                  sur l'ancienne famille — alors que l'etape 1 affiche la
 *                  nouvelle. Cet ecart se lit comme un bug.
 */
/**
 * Lu a l'evaluation du module, donc avant qu'aucun composant ne tourne.
 *
 * `<go-variant-url>` ecrit `?variant=` en `replaceState` des sa connexion :
 * relire l'URL plus tard reviendrait a prendre notre propre ecriture pour une
 * demande du visiteur, et l'ouverture sur la famille la moins chere ne se
 * ferait jamais. D'ou l'ordre de chargement impose dans les sections —
 * go-config-steps.js AVANT go-variant-url.js.
 */
const ASKED_VARIANT = new URLSearchParams(window.location.search).get('variant');

/**
 * Sections deja ouvertes sur leur famille par defaut, par identifiant.
 *
 * La Section Rendering API remplace le configurateur a chaque changement, donc
 * reconnecte ce composant : sans cette trace, le defaut se rappliquerait a
 * chaque choix du visiteur et le ramenerait sans fin sur la famille la moins
 * chere.
 */
const defaulted = new Set();

class GoConfigSteps extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    this.reveal();

    // Meme attente que `<go-variant-url>` : `VariantSelects` touche a
    // `window.StandardEvents` (analytics Shopify, charge en parallele) a chaque
    // `change`. Emis trop tot, l'evenement leve une exception DANS le
    // gestionnaire de Dawn, avant son `publish` : la valeur est cochee mais ni
    // le prix ni la galerie ne suivent.
    this.whenReady(() => this.openDefault());
  }

  whenReady(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    window.addEventListener('load', callback, { once: true });
  }

  /**
   * Ouvre le configurateur sur la famille la moins chere.
   *
   * Aucun nom de famille n'est ecrit ici, et aucun prix n'est relu : les
   * familles sont rendues triees par prix croissant par
   * `snippets/go-fabric-families.liquid`, donc **la premiere du DOM est la moins
   * chere**. Une retarification dans l'admin change l'ordre au rendu suivant, et
   * le defaut suit tout seul.
   *
   * Deux choix explicites passent devant, dans cet ordre :
   *   1. `?variant=` dans l'URL — lien partage, retour arriere, campagne ;
   *   2. le reglage « variante par defaut » de la section, s'il est renseigne.
   *
   * Contrepartie assumee : le serveur rend d'abord la variante que Shopify
   * considere selectionnee (la premiere disponible dans l'ordre de l'admin). Si
   * elle n'est pas dans la famille la moins chere, le prix bascule une fois au
   * chargement. Ranger cette variante en tete dans l'admin supprime la bascule
   * sans rien changer au theme.
   */
  openDefault() {
    const section = this.closest('variant-selects')?.dataset.section || '';
    if (defaulted.has(section)) return;
    defaulted.add(section);

    if (ASKED_VARIANT) return;

    const declared = document.querySelector(`go-variant-url[data-section="${section}"]`)?.dataset.defaultVariant;
    if (declared) return;

    const cheapest = this.querySelector('[data-family-filter]');
    if (!cheapest) return;

    // Meme couple qu'un clic sur un bouton de famille : ouvrir l'affichage, puis
    // cocher un coloris — `select` ne fait rien si la famille est deja celle du
    // coloris coche, donc aucun re-rendu inutile quand le serveur tombe juste.
    this.apply(cheapest.dataset.familyFilter);
    this.select(cheapest.dataset.familyFilter);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const button = event.target.closest('[data-family-filter]');
    if (!button || !this.contains(button)) return;

    this.apply(button.dataset.familyFilter);
    this.select(button.dataset.familyFilter);
  }

  apply(family) {
    this.querySelectorAll('[data-family-filter]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.familyFilter === family));
    });

    this.querySelectorAll('.go-group[data-family]').forEach((group) => {
      group.toggleAttribute('hidden', group.dataset.family !== family);
    });

    this.reveal(family);
  }

  /**
   * Ramene le coloris coche dans la partie visible de la rangee.
   *
   * Sous 990px l'etape 2 defile horizontalement, quatre pastilles a la fois. Or
   * la rangee repart de zero a chaque re-rendu du configurateur par la Section
   * Rendering API : passe la quatrieme pastille, la valeur qui vient d'etre
   * choisie se retrouverait hors champ juste apres l'avoir choisie. Meme chose
   * a l'ouverture d'une famille dont le coloris retenu est loin dans la liste.
   *
   * `scrollLeft` et non `scrollIntoView` : ce dernier fait aussi defiler les
   * ancetres, donc la page entiere, alors qu'on ne recale ici qu'une rangee.
   */
  reveal(family) {
    const group = family ? this.group(family) : this.querySelector('.go-group[data-family]:not([hidden])');
    const viewport = group?.querySelector('[data-carousel-viewport]');
    const input = group?.querySelector('input[type="radio"]:checked');
    if (!viewport || !input) return;

    const bounds = viewport.getBoundingClientRect();
    const target = input.closest('.go-swatch').getBoundingClientRect();

    // Deja entierement visible — le cas de l'ordinateur, ou la rangee ne defile
    // pas du tout. Recaler sans raison deplacerait les pastilles sous les yeux.
    if (target.left >= bounds.left && target.right <= bounds.right) return;

    viewport.scrollLeft += target.left - bounds.left;
  }

  group(family) {
    return Array.from(this.querySelectorAll('.go-group[data-family]')).find(
      (group) => group.dataset.family === family
    );
  }

  select(family) {
    const inputs = Array.from(this.group(family)?.querySelectorAll('input[type="radio"]') || []);
    if (!inputs.length) return;

    // Deja dans la famille : la valeur cochee est le bon coloris, la recocher
    // ne ferait que declencher un re-rendu identique.
    if (inputs.some((input) => input.checked)) return;

    // Le premier coloris DISPONIBLE, pas le premier tout court : ouvrir une
    // famille sur un coloris epuise afficherait d'emblee un bouton d'ajout
    // desactive, alors que le reste de la famille est en stock.
    const target = inputs.find((input) => !input.classList.contains('disabled')) || inputs[0];

    // Un vrai clic, pas `checked = true` : c'est l'evenement `change` qui
    // reveille VariantSelects, donc le prix, le visuel et la barre mobile.
    target.click();
  }
}

// Meme garde que les autres composants GO! : un configurateur peut apparaitre
// plus d'une fois sur une page (`featured-product` est une section, donc
// repetable), et deux balises <script src> identiques s'executent deux fois.
if (!customElements.get('go-config-steps')) {
  customElements.define('go-config-steps', GoConfigSteps);
}
